import { useState, FormEvent } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { LoginFormData, LoginFormErrors } from '../types';

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => void;
  loading: boolean;
  errors: LoginFormErrors;
}

const LoginForm = ({ onSubmit, loading, errors }: LoginFormProps) => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<LoginFormErrors>({});

  const validateEmail = (email: string): string | undefined => {
    if (!email) return 'Email é obrigatório';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Email inválido';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Senha é obrigatória';
    if (password.length < 6) return 'Senha deve ter no mínimo 6 caracteres';
    return undefined;
  };

  const handleEmailBlur = () => {
    const error = validateEmail(formData.email);
    setFieldErrors(prev => ({ ...prev, email: error }));
  };

  const handlePasswordBlur = () => {
    const error = validatePassword(formData.password);
    setFieldErrors(prev => ({ ...prev, password: error }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    
    if (emailError || passwordError) {
      setFieldErrors({
        email: emailError,
        password: passwordError
      });
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors.general}</p>
        </div>
      )}

      <Input
        type="email"
        label="Email Corporativo"
        placeholder="seu.email@empresa.com.br"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        onBlur={handleEmailBlur}
        error={fieldErrors.email || errors.email}
        required
        disabled={loading}
        autoComplete="email"
      />

      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          label="Senha"
          placeholder="Digite sua senha"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          onBlur={handlePasswordBlur}
          error={fieldErrors.password || errors.password}
          required
          disabled={loading}
          autoComplete="current-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 p-1 hover:bg-muted rounded transition-colors duration-150"
          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          disabled={loading}
        >
          <Icon
            name={showPassword ? 'EyeOff' : 'Eye'}
            size={18}
            color="var(--color-muted-foreground)"
          />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <Checkbox
          label="Lembrar-me"
          checked={formData.rememberMe}
          onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
          disabled={loading}
        />
        <button
          type="button"
          className="text-sm text-primary hover:text-primary/80 transition-colors duration-150"
          disabled={loading}
        >
          Esqueceu a senha?
        </button>
      </div>

      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={loading}
        iconName="LogIn"
        iconPosition="right"
      >
        Entrar no Sistema
      </Button>
    </form>
  );
};

export default LoginForm;